/**
 * Moteur de scrutin (SPECIFICATION.md §6).
 *
 * Fonctions pures : à entrées égales, résultat identique, quel que soit
 * l'ordre d'arrivée des bulletins. C'est la condition pour qu'un journal
 * rejoué produise exactement le même procès-verbal.
 */

import type {
  BallotRecord,
  Item,
  NulReason,
  RoundResult,
  VoixItem,
} from './types.js';

export interface TallyInput {
  readonly round: 1 | 2;
  /** Items soumis au vote pour ce tour. Un item hors liste rend le bulletin nul. */
  readonly items: readonly Item[];
  readonly ballots: readonly BallotRecord[];
  readonly votesPerElector: number;
  readonly absoluteMajority: boolean;
  readonly inscrits: number;
}

/**
 * Dépouille un tour.
 *
 * Qualifications possibles d'un bulletin (§6.2) :
 * - 2 choix distincts connus → valide, complet ;
 * - 1 choix connu → valide, partiel ;
 * - aucun choix → blanc ;
 * - item répété, item inconnu, voix en excès, jeton déjà utilisé → nul, motivé.
 */
export function tallyRound(input: TallyInput): RoundResult {
  const knownItems = new Set(input.items.map((item) => item.id));
  const voix = new Map<string, number>();
  for (const item of input.items) voix.set(item.id, 0);

  const nulsParMotif: Record<string, number> = {};
  const jetonDejaVu = new Set<string>();

  let valides = 0;
  let complets = 0;
  let partiels = 0;
  let blancs = 0;
  let nuls = 0;
  let totalVoix = 0;

  const compterNul = (motif: NulReason): void => {
    nuls += 1;
    nulsParMotif[motif] = (nulsParMotif[motif] ?? 0) + 1;
  };

  // Ordre déterministe : horodatage croissant, puis ordre de dépôt.
  const ordonnes = input.ballots
    .map((ballot, index) => ({ ballot, index }))
    .sort((a, b) => a.ballot.ts - b.ballot.ts || a.index - b.index);

  for (const { ballot } of ordonnes) {
    if (jetonDejaVu.has(ballot.tokenHash)) {
      compterNul('jeton_double');
      continue;
    }
    jetonDejaVu.add(ballot.tokenHash);

    if (ballot.choices.length === 0) {
      blancs += 1;
      continue;
    }
    if (ballot.choices.length > input.votesPerElector) {
      compterNul('voix_en_exces');
      continue;
    }
    if (new Set(ballot.choices).size !== ballot.choices.length) {
      compterNul('item_repete');
      continue;
    }
    if (ballot.choices.some((id) => !knownItems.has(id))) {
      compterNul('item_inconnu');
      continue;
    }

    valides += 1;
    if (ballot.choices.length === input.votesPerElector) complets += 1;
    else partiels += 1;

    for (const id of ballot.choices) {
      voix.set(id, (voix.get(id) ?? 0) + 1);
      totalVoix += 1;
    }
  }

  // Seuil de la majorité absolue : `2 × voix > E` équivaut à `voix ≥ ⌊E/2⌋ + 1`.
  // Au second tour, la majorité relative suffit : il n'y a aucun seuil.
  const seuil =
    input.absoluteMajority && valides > 0 ? Math.floor(valides / 2) + 1 : null;

  const classement: VoixItem[] = input.items
    .map((item) => ({ itemId: item.id, label: item.label, voix: voix.get(item.id) ?? 0 }))
    .sort((a, b) => b.voix - a.voix || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0));

  const majoritaires =
    input.absoluteMajority && seuil !== null
      ? classement.filter((entry) => entry.voix >= seuil).map((entry) => entry.itemId)
      : [];

  return {
    round: input.round,
    voixParElecteur: input.votesPerElector,
    inscrits: input.inscrits,
    votants: valides + blancs,
    valides,
    complets,
    partiels,
    blancs,
    nuls,
    nulsParMotif,
    classement,
    majoritaires,
    seuil,
    totalVoix,
  };
}

export interface Selection {
  readonly elected: readonly string[];
  /**
   * Items à égalité parfaite au seuil d'attribution. Non vide ⇒ un départage
   * manuel est obligatoire : on ne tranche jamais une égalité par le code.
   */
  readonly tied: readonly string[];
}

/**
 * Attribue `seatsToFill` sièges au vu d'un classement.
 *
 * Au premier tour, seuls les items atteignant la majorité absolue concourent.
 * Au second tour, la majorité relative suffit : le classement est pris tel quel.
 */
export function selectSeats(
  ranking: readonly VoixItem[],
  seatsToFill: number,
  options: { readonly absoluteMajority: boolean; readonly seuil: number | null },
): Selection {
  if (seatsToFill <= 0) return { elected: [], tied: [] };

  const eligibles = options.absoluteMajority
    ? ranking.filter((entry) => options.seuil !== null && entry.voix >= (options.seuil as number))
    : [...ranking];

  if (eligibles.length === 0) return { elected: [], tied: [] };
  if (eligibles.length <= seatsToFill) {
    return { elected: eligibles.map((entry) => entry.itemId), tied: [] };
  }

  const seuilCoupe = (eligibles[seatsToFill - 1] as VoixItem).voix;
  const auDessus = eligibles.filter((entry) => entry.voix > seuilCoupe);
  const aLaCoupe = eligibles.filter((entry) => entry.voix === seuilCoupe);

  // Aucune ambiguïté : les items restants ont strictement moins de voix.
  if (auDessus.length + aLaCoupe.length <= seatsToFill) {
    return { elected: eligibles.slice(0, seatsToFill).map((entry) => entry.itemId), tied: [] };
  }

  const restants = seatsToFill - auDessus.length;
  if (restants >= aLaCoupe.length) {
    return { elected: eligibles.slice(0, seatsToFill).map((entry) => entry.itemId), tied: [] };
  }

  return {
    elected: auDessus.map((entry) => entry.itemId),
    tied: aLaCoupe.map((entry) => entry.itemId),
  };
}

/** Total des voix attribuées à un item. */
export function voixOf(result: RoundResult, itemId: string): number {
  return result.classement.find((entry) => entry.itemId === itemId)?.voix ?? 0;
}
